/**
 * Memory Storage Adapter
 * 
 * In-memory fallback when all other adapters fail.
 * Data is lost when page refreshes or tab closes.
 * 
 * This is the last resort adapter.
 * 
 * Pros:
 * - Always available
 * - Fast
 * - No browser restrictions
 * 
 * Cons:
 * - NOT persistent (data lost on refresh)
 * - Only works for current session
 * - Limited by JS heap size
 */

import type { StorageAdapter } from '../types';

/**
 * In-memory storage adapter
 */
class MemoryStorageAdapter implements StorageAdapter {
  readonly name = 'memory';

  private storage = new Map<string, string>();

  async get(key: string): Promise<string | null> {
    return this.storage.get(key) ?? null;
  }

  async set(key: string, value: string): Promise<void> {
    this.storage.set(key, value);
  }

  async remove(key: string): Promise<void> {
    this.storage.delete(key);
  }

  async clear(): Promise<void> {
    this.storage.clear();
  }

  async keys(): Promise<string[]> {
    return Array.from(this.storage.keys());
  }

  async isAvailable(): Promise<boolean> {
    // Always available
    return true;
  }
}

/**
 * Singleton instance
 */
export const memoryStorageAdapter = new MemoryStorageAdapter();
